export const kExternalStorageProviders = ['google_drive', 'dropbox', 'box', 'onedrive', 'clio', 'sharepoint'] as const

export type ExternalStorageProvider = (typeof kExternalStorageProviders)[number]

export const kExternalStorageProvider = {
  GOOGLE_DRIVE: 'google_drive',
  DROPBOX: 'dropbox',
  BOX: 'box',
  ONEDRIVE: 'onedrive',
  CLIO: 'clio',
  SHAREPOINT: 'sharepoint',
} as const satisfies Record<string, ExternalStorageProvider>

export const kExternalStorageProviderDisplayName: Record<ExternalStorageProvider, string> = {
  [kExternalStorageProvider.GOOGLE_DRIVE]: 'Google Drive',
  [kExternalStorageProvider.DROPBOX]: 'Dropbox',
  [kExternalStorageProvider.BOX]: 'Box',
  [kExternalStorageProvider.ONEDRIVE]: 'Microsoft OneDrive',
  [kExternalStorageProvider.CLIO]: 'Clio',
  [kExternalStorageProvider.SHAREPOINT]: 'SharePoint',
}

/**
 * Single source of truth for provider availability in the UI.
 *
 * Clio is available to named pilot organizations through its separate org gate.
 */
const kEnabledExternalStorageProvidersByMode: Record<string, readonly ExternalStorageProvider[]> = {
  development: kExternalStorageProviders,
  staging: kExternalStorageProviders,
  ephemeral: kExternalStorageProviders,
  production: [kExternalStorageProvider.BOX, kExternalStorageProvider.ONEDRIVE, kExternalStorageProvider.DROPBOX, kExternalStorageProvider.SHAREPOINT],
}

export function getEnabledExternalStorageProvidersForMode(mode: string): readonly ExternalStorageProvider[] {
  return kEnabledExternalStorageProvidersByMode[mode] ?? []
}

/**
 * Organizations allowed to see/connect Clio in production (single-customer
 * pilot, ENG-7047). Non-production modes enable Clio for everyone via
 * {@link kEnabledExternalStorageProvidersByMode}, so this only gates prod. The
 * backend enforces the same allowlist on the Clio `/authorize` endpoint keyed
 * on the identical `organization_id` claim.
 */
const kClioAllowedOrgIdsByMode: Record<string, readonly string[]> = {
  // Berenji Divorce & Family Law Group
  production: ['563e93a0-d30c-4355-a1a1-8858ed1cd310'],
}

export const kClioAllowedOrgIds: readonly string[] = kClioAllowedOrgIdsByMode[import.meta.env.MODE] ?? []

/**
 * Providers whose availability is decided per organization rather than by env mode.
 *
 * Both the settings hook (which filters the tile out entirely for organizations
 * that are not allowlisted) and ConnectedAccounts (which decides whether a shown
 * tile is connectable or an inert "Coming soon" row) must agree. They are keyed
 * off this one list so an org-gated provider cannot be admitted by one and
 * refused by the other.
 */
export const kOrgGatedExternalStorageProviders: readonly ExternalStorageProvider[] = [kExternalStorageProvider.CLIO]

export function isOrgGatedExternalStorageProvider(provider: ExternalStorageProvider): boolean {
  return kOrgGatedExternalStorageProviders.includes(provider)
}

const kEnabledExternalStorageProviderSet = new Set<ExternalStorageProvider>(getEnabledExternalStorageProvidersForMode(import.meta.env.MODE))

export const kExternalStorageProviderEnabled: Record<ExternalStorageProvider, boolean> = {
  [kExternalStorageProvider.GOOGLE_DRIVE]: kEnabledExternalStorageProviderSet.has(kExternalStorageProvider.GOOGLE_DRIVE),
  [kExternalStorageProvider.DROPBOX]: kEnabledExternalStorageProviderSet.has(kExternalStorageProvider.DROPBOX),
  [kExternalStorageProvider.BOX]: kEnabledExternalStorageProviderSet.has(kExternalStorageProvider.BOX),
  [kExternalStorageProvider.ONEDRIVE]: kEnabledExternalStorageProviderSet.has(kExternalStorageProvider.ONEDRIVE),
  [kExternalStorageProvider.CLIO]: kEnabledExternalStorageProviderSet.has(kExternalStorageProvider.CLIO),
  [kExternalStorageProvider.SHAREPOINT]: kEnabledExternalStorageProviderSet.has(kExternalStorageProvider.SHAREPOINT),
}

export const kEnabledExternalStorageProviders = kExternalStorageProviders.filter((provider) => kExternalStorageProviderEnabled[provider])

// Membership in the full provider tuple ("is this a known provider"), NOT whether
// it is enabled — used to validate a stored pending provider before its connect
// toast, which must fire for allowlisted providers (e.g. Clio) that aren't in the
// global enabled set. Use kIsExternalStorageProviderEnabled for enablement.
export function kIsExternalStorageProvider(value: string | null): value is ExternalStorageProvider {
  return kExternalStorageProviders.some((provider) => provider === value)
}

export function kIsExternalStorageProviderEnabled(provider: ExternalStorageProvider): boolean {
  return kExternalStorageProviderEnabled[provider]
}

export const kHasEnabledExternalStorageProviders = kEnabledExternalStorageProviders.length > 0

export function sortExternalStorageProvidersForDisplay(
  providers: readonly ExternalStorageProvider[],
  enabledProviders: readonly ExternalStorageProvider[] = kEnabledExternalStorageProviders
): ExternalStorageProvider[] {
  const enabledProviderSet = new Set(enabledProviders)

  return [...providers].sort((providerA, providerB) => {
    const providerAEnabled = enabledProviderSet.has(providerA)
    const providerBEnabled = enabledProviderSet.has(providerB)

    if (providerAEnabled !== providerBEnabled) {
      return providerAEnabled ? -1 : 1
    }

    return kExternalStorageProviderDisplayName[providerA].localeCompare(kExternalStorageProviderDisplayName[providerB])
  })
}

export const kVisibleExternalStorageProviders = sortExternalStorageProvidersForDisplay(kExternalStorageProviders)
